import type { ILandingCustomModuleNavRef } from './landingButtonCustomModule'
import type { ILandingTemplateButton } from './landingTemplateButtons'
import type { ProjectRouteIdentity } from '~/shared/lib'
import { resolveEventModuleRoute } from '~/entities/round-table/shared/resolveEventModuleRoute'
import { resolveModuleKindBySlug } from '~/entities/round-table/shared/resolveModuleKindBySlug'
import { CustomModuleBaseTypeEnum } from '~/shared/api'
import { ProjectModuleSlugEnum } from '~/shared/const'
import { isMySpeechSlug } from '~/shared/const/workspaceModuleCreate'
import { getProjectRouteParam, permissions } from '~/shared/lib'
import { resolveLandingCustomModuleFromButton } from './landingButtonCustomModule'
import { normalizeWorkspaceModuleSlug } from './workspaceModuleVisibility'

interface LandingModuleListRoute {
  name: string
  query?: Record<string, string>
  requiresProjectParam?: boolean
}

export function resolveLandingModuleListRoute(
  moduleSlug: string,
  moduleType?: CustomModuleBaseTypeEnum | null,
): LandingModuleListRoute | null {
  const slug = normalizeWorkspaceModuleSlug(moduleSlug)

  if (isMySpeechSlug(slug)) {
    return {
      name: permissions.PROJECTS.WORKSPACE.MY_SPEECH.PERMISSION,
      query: { moduleSlug: slug },
    }
  }

  const nativeRoute = resolveNativeLandingModuleListRoute(slug)
  if (nativeRoute) {
    return nativeRoute
  }

  if (!moduleType) {
    return null
  }

  return resolveCustomModuleListRoute({
    moduleSlug: slug,
    moduleType,
  })
}

function resolveNativeLandingModuleListRoute(
  slug: string,
): LandingModuleListRoute | null {
  switch (slug) {
    case ProjectModuleSlugEnum.AboutProject:
      return { name: permissions.PROJECTS.PROJECT.PERMISSION }
    case ProjectModuleSlugEnum.ProjectMembers:
      return { name: permissions.PROJECTS.WORKSPACE.MEMBERS.PERMISSION }
    case ProjectModuleSlugEnum.ProjectManagement:
      return { name: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.SETTINGS.PERMISSION }
    case ProjectModuleSlugEnum.Supporters:
      return { name: permissions.PROJECTS.WORKSPACE.SUPPORTERS.PERMISSION }
    case ProjectModuleSlugEnum.ProjectNews:
      return {
        name: permissions.PROJECTS.WORKSPACE.NEWS.PERMISSION,
        query: { moduleSlug: slug },
      }
    case ProjectModuleSlugEnum.ProjectSpeeches:
      return {
        name: permissions.PROJECTS.WORKSPACE.MY_SPEECH.PERMISSION,
        query: { moduleSlug: slug },
      }
    case ProjectModuleSlugEnum.ProjectSurvey:
      return { name: permissions.PROJECTS.WORKSPACE.SURVEYS.PERMISSION }
    case ProjectModuleSlugEnum.ProjectOrgMail:
      return { name: permissions.PROJECTS.WORKSPACE.MAIL.PERMISSION }
    case 'question_to_deputy':
      return { name: permissions.PROJECTS.WORKSPACE.QUESTION_TO_DEPUTY.PERMISSION }
    case 'service_phones':
      return { name: permissions.PROJECTS.WORKSPACE.EMERGENCY_CONTACTS.PERMISSION }
    case 'project_counters':
      return { name: permissions.PROJECTS.WORKSPACE.COUNTERS.PERMISSION }
    case 'consumer_rights':
      return { name: permissions.PROJECTS.WORKSPACE.RIGHTS_OF_CONSUMER.PERMISSION }
    case 'accident_guidance':
      return {
        name: permissions.PROJECTS.WORKSPACE.WHAT_TO_DO_IN_CASE_OF_ACCIDENT.PERMISSION,
      }
    case ProjectModuleSlugEnum.ProjectCalendar:
      return {
        name: permissions.PROJECTS.WORKSPACE.CALENDAR.PERMISSION,
        query: { moduleSlug: slug },
        requiresProjectParam: true,
      }
    case 'entertainment':
      return {
        name: permissions.ENTERTAINMENT.PERMISSION,
        query: { moduleSlug: slug },
        requiresProjectParam: false,
      }
    default:
      return null
  }
}

function resolveCustomModuleListRoute(
  mod: ILandingCustomModuleNavRef,
): LandingModuleListRoute | null {
  const query = { moduleSlug: mod.moduleSlug }

  if (isMySpeechSlug(mod.moduleSlug)) {
    return {
      name: permissions.PROJECTS.WORKSPACE.MY_SPEECH.PERMISSION,
      query,
    }
  }

  switch (mod.moduleType) {
    case CustomModuleBaseTypeEnum.News:
      return {
        name: permissions.PROJECTS.WORKSPACE.NEWS.PERMISSION,
        query,
      }
    case CustomModuleBaseTypeEnum.Survey:
      return { name: permissions.PROJECTS.WORKSPACE.SURVEYS.PERMISSION, query }
    case CustomModuleBaseTypeEnum.Supporters:
      return { name: permissions.PROJECTS.WORKSPACE.SUPPORTERS.PERMISSION, query }
    case CustomModuleBaseTypeEnum.Mail:
      return { name: permissions.PROJECTS.WORKSPACE.MAIL.PERMISSION, query }
    case CustomModuleBaseTypeEnum.ObjectPassport: {
      const kind = resolveModuleKindBySlug(mod.moduleSlug)
      if (!kind) {
        return {
          name: permissions.PROJECTS.WORKSPACE.PERMISSION,
          query,
        }
      }
      return {
        name: resolveEventModuleRoute(kind, '', mod.moduleSlug, 'entry').name,
        query,
      }
    }
    default:
      return {
        name: permissions.PROJECTS.WORKSPACE.PERMISSION,
        query,
      }
  }
}

export function resolveLandingModuleObjectDetailRoute(
  project: ProjectRouteIdentity | string,
  button: Pick<ILandingTemplateButton, 'moduleSlug' | 'moduleObjectId' | 'moduleType'>,
): { name: string, params: Record<string, string>, query?: Record<string, string> } | null {
  const routeParam = typeof project === 'string'
    ? project
    : getProjectRouteParam(project)
  const objectId = button.moduleObjectId?.trim()
  if (!objectId) {
    return null
  }

  const slug = normalizeWorkspaceModuleSlug(button.moduleSlug)
  const query = { moduleSlug: slug }

  if (isMySpeechSlug(slug)) {
    return {
      name: permissions.PROJECTS.WORKSPACE.MY_SPEECH.DETAIL.PERMISSION,
      params: { id: routeParam, newsId: objectId },
      query,
    }
  }

  switch (slug) {
    case ProjectModuleSlugEnum.ProjectNews:
      return {
        name: permissions.PROJECTS.WORKSPACE.NEWS.DETAIL.PERMISSION,
        params: { id: routeParam, newsId: objectId },
        query,
      }
    case ProjectModuleSlugEnum.ProjectSpeeches:
      return {
        name: permissions.PROJECTS.WORKSPACE.MY_SPEECH.DETAIL.PERMISSION,
        params: { id: routeParam, newsId: objectId },
        query,
      }
    case ProjectModuleSlugEnum.ProjectSurvey:
      return {
        name: permissions.PROJECTS.WORKSPACE.SURVEYS.FILL.PERMISSION,
        params: { id: routeParam, surveyId: objectId },
        query,
      }
    case ProjectModuleSlugEnum.Supporters:
      return {
        name: permissions.PROJECTS.WORKSPACE.SUPPORTERS.PERMISSION,
        params: { id: routeParam },
        query: { ...query, supporterId: objectId },
      }
    case ProjectModuleSlugEnum.ProjectOrgMail:
      return {
        name: permissions.PROJECTS.WORKSPACE.MAIL.APPEAL.PERMISSION,
        params: { id: routeParam, appealId: objectId },
        query,
      }
    case ProjectModuleSlugEnum.ProjectMembers:
      return {
        name: permissions.PROJECTS.WORKSPACE.MEMBERS.PERMISSION,
        params: { id: routeParam },
        query: { ...query, user: objectId },
      }
    case 'entertainment':
      return {
        name: permissions.ENTERTAINMENT.DETAIL.PERMISSION,
        params: { newsId: objectId },
        query,
      }
    case 'consumer_rights':
      return {
        name: permissions.PROJECTS.WORKSPACE.RIGHTS_OF_CONSUMER.DETAIL.PERMISSION,
        params: { id: routeParam, newsId: objectId },
        query,
      }
    case 'accident_guidance':
      return {
        name: permissions.PROJECTS.WORKSPACE.WHAT_TO_DO_IN_CASE_OF_ACCIDENT.DETAIL.PERMISSION,
        params: { id: routeParam, newsId: objectId },
        query,
      }
    case 'question_to_deputy':
      return {
        name: permissions.PROJECTS.WORKSPACE.QUESTION_TO_DEPUTY.FILL.PERMISSION,
        params: { id: routeParam, surveyId: objectId },
        query,
      }
    default: {
      const customModule = resolveLandingCustomModuleFromButton(button)

      if (!customModule) {
        return null
      }

      return resolveCustomModuleObjectDetailRoute(routeParam, objectId, customModule)
    }
  }
}

function resolveCustomModuleObjectDetailRoute(
  routeParam: string,
  objectId: string,
  mod: ILandingCustomModuleNavRef,
): { name: string, params: Record<string, string>, query?: Record<string, string> } | null {
  const query = { moduleSlug: mod.moduleSlug }

  if (isMySpeechSlug(mod.moduleSlug)) {
    return {
      name: permissions.PROJECTS.WORKSPACE.MY_SPEECH.DETAIL.PERMISSION,
      params: { id: routeParam, newsId: objectId },
      query,
    }
  }

  switch (mod.moduleType) {
    case CustomModuleBaseTypeEnum.News:
      return {
        name: permissions.PROJECTS.WORKSPACE.NEWS.DETAIL.PERMISSION,
        params: { id: routeParam, newsId: objectId },
        query,
      }
    case CustomModuleBaseTypeEnum.Survey:
      return {
        name: permissions.PROJECTS.WORKSPACE.SURVEYS.FILL.PERMISSION,
        params: { id: routeParam, surveyId: objectId },
        query,
      }
    case CustomModuleBaseTypeEnum.Supporters:
      return {
        name: permissions.PROJECTS.WORKSPACE.SUPPORTERS.PERMISSION,
        params: { id: routeParam },
        query: { ...query, supporterId: objectId },
      }
    case CustomModuleBaseTypeEnum.Mail:
      return {
        name: permissions.PROJECTS.WORKSPACE.MAIL.APPEAL.PERMISSION,
        params: { id: routeParam, appealId: objectId },
        query,
      }
    default:
      return null
  }
}
